import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, UserPlus, Shield, Users as UsersIcon } from "lucide-react";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { InviteUserDialog } from "@/components/settings/InviteUserDialog";

interface UserWithRoles {
  user_id: string;
  email: string;
  org_role: string;
  family_roles: Array<{
    family_id: string;
    family_name: string;
    role: string;
  }>;
}

export default function UsersSettings() {
  const { orgId, isMFO, families } = useTenant();
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);

  const { data: orgUsers = [], isLoading, refetch } = useQuery({
    queryKey: ['org-users', orgId],
    queryFn: async () => {
      if (!orgId) return [];

      // Get all org roles for this organization
      const { data: orgRoles, error: orgError } = await supabase
        .from('users_org_roles')
        .select('user_id, role')
        .eq('org_id', orgId);

      if (orgError) throw orgError;

      const userIds = orgRoles?.map(r => r.user_id) || [];

      // Get family roles for these users
      const { data: familyRoles, error: familyError } = await supabase
        .from('users_family_roles')
        .select('user_id, family_id, role')
        .in('user_id', userIds);

      if (familyError) throw familyError;

      // Get accepted invitations to look up emails
      const { data: acceptedInvites } = await supabase
        .from('family_invitations')
        .select('email, accepted_by')
        .in('accepted_by', userIds)
        .not('accepted_by', 'is', null);

      // Build a map of user_id -> email from invitations
      const userEmailMap = new Map<string, string>();
      (acceptedInvites || []).forEach(inv => {
        if (inv.accepted_by && inv.email) {
          userEmailMap.set(inv.accepted_by, inv.email);
        }
      });

      // Get family names
      const familyMap = new Map(families.map(f => [f.id, f.name]));

      // Combine the data
      const usersWithRoles: UserWithRoles[] = orgRoles.map(orgRole => ({
        user_id: orgRole.user_id,
        email: userEmailMap.get(orgRole.user_id) || `user-${orgRole.user_id.slice(0, 8)}...`,
        org_role: orgRole.role,
        family_roles: (familyRoles || [])
          .filter(fr => fr.user_id === orgRole.user_id)
          .map(fr => ({
            family_id: fr.family_id,
            family_name: familyMap.get(fr.family_id) || 'Unknown',
            role: fr.role,
          })),
      }));

      return usersWithRoles;
    },
    enabled: !!orgId,
  });

  const getRoleBadgeVariant = (role: string) => {
    if (role.includes('Admin')) return 'default';
    if (role.includes('Manager')) return 'secondary';
    if (role.includes('Analyst')) return 'outline';
    return 'outline';
  };

  const getRoleLabel = (role: string) => {
    return role.replace('Org', '').replace('Family', '');
  };

  return (
    <div className="flex-1 space-y-4 px-6 py-6">
      
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Users & Roles</h1>
          <p className="text-muted-foreground">
            {isMFO 
              ? "Manage MFO team members and client family users"
              : "Manage internal team members and family principals"
            }
          </p>
        </div>
        <Button onClick={() => setInviteDialogOpen(true)}>
          <UserPlus className="h-4 w-4" />
          Invite User
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{orgUsers.length}</div>
            <p className="text-xs text-muted-foreground">Active users</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">
              {isMFO ? "MFO Team" : "Team Members"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {orgUsers.filter(u => u.org_role.includes('Admin') || u.org_role.includes('Manager')).length}
            </div>
            <p className="text-xs text-muted-foreground">Admins & managers</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">
              {isMFO ? "Client Users" : "Principals"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {orgUsers.filter(u => u.family_roles.length > 0).length}
            </div>
            <p className="text-xs text-muted-foreground">With family access</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="all" className="space-y-4">
        <TabsList>
          <TabsTrigger value="all">All Users</TabsTrigger>
          <TabsTrigger value="org">
            <Shield className="h-4 w-4" />
            {isMFO ? "MFO Team" : "Organization"}
          </TabsTrigger>
          {isMFO && (
            <TabsTrigger value="family">
              <UsersIcon className="h-4 w-4" />
              Family Users
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="all" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>All Users</CardTitle>
              <CardDescription>
                Complete list of users with their roles and permissions
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="text-center py-8 text-muted-foreground">Loading users...</div>
              ) : orgUsers.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No users found. Invite your first user to get started.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Organization Role</TableHead>
                      <TableHead>Family Access</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orgUsers.map((user) => (
                      <TableRow key={user.user_id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{user.email}</div>
                            <div className="text-xs text-muted-foreground">
                              ID: {user.user_id.slice(0, 8)}...
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={getRoleBadgeVariant(user.org_role)}>
                            {getRoleLabel(user.org_role)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {user.family_roles.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {user.family_roles.map((fr, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs">
                                  {fr.family_name}: {getRoleLabel(fr.role)}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm">Edit</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="org" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{isMFO ? "MFO Team Members" : "Organization Members"}</CardTitle>
              <CardDescription>
                Users with organization-level access and permissions
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Permissions</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orgUsers.map((user) => (
                    <TableRow key={user.user_id}>
                      <TableCell className="font-medium">{user.email}</TableCell>
                      <TableCell>
                        <Badge variant={getRoleBadgeVariant(user.org_role)}>
                          {getRoleLabel(user.org_role)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {user.org_role === 'OrgAdmin' && "Full access"}
                        {user.org_role === 'OrgManager' && "Manage data & users"}
                        {user.org_role === 'OrgAnalyst' && "View & edit data"}
                        {user.org_role === 'OrgViewer' && "Read-only"}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm">Edit</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {isMFO && (
          <TabsContent value="family" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Family Users</CardTitle>
                <CardDescription>
                  Client users with access to specific family portfolios
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Family</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orgUsers
                      .filter(u => u.family_roles.length > 0)
                      .flatMap(user => 
                        user.family_roles.map((fr, idx) => (
                          <TableRow key={`${user.user_id}-${idx}`}>
                            <TableCell className="font-medium">{user.email}</TableCell>
                            <TableCell>{fr.family_name}</TableCell>
                            <TableCell>
                              <Badge variant={getRoleBadgeVariant(fr.role)}>
                                {getRoleLabel(fr.role)}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <Button variant="ghost" size="sm">Edit</Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      <InviteUserDialog
        open={inviteDialogOpen}
        onOpenChange={setInviteDialogOpen}
        onSuccess={refetch}
      />
    </div>
  );
}
